'use client';

import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { cn } from '../../../lib/utils';
import { useComboboxAnchorContext } from './combobox-context';

export type ComboboxContentProps = Omit<ComboboxPrimitive.Popup.Props, 'className'> & {
  className?: string;
  side?: ComboboxPrimitive.Positioner.Props['side'];
  sideOffset?: ComboboxPrimitive.Positioner.Props['sideOffset'];
  align?: ComboboxPrimitive.Positioner.Props['align'];
  anchor?: ComboboxPrimitive.Positioner.Props['anchor'];
};

export const ComboboxContent = ({
  className,
  side = 'bottom',
  sideOffset = 8,
  align = 'start',
  anchor: anchorProp,
  children,
  ...props
}: ComboboxContentProps) => {
  // anchorContext is null in single-select (no ComboboxChips to populate it);
  // Base UI positioner treats { current: null } as no anchor and falls back to default.
  const anchorContext = useComboboxAnchorContext();
  const anchor = anchorProp ?? anchorContext;

  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        anchor={anchor}
        // z-[10001] — must exceed aura modals overlay/container z-[10000] so dropdowns render above the modal
        // pointer-events-auto — Radix Dialog (modal=true) sets pointer-events:none on body siblings incl. portals; restore it here
        className="pointer-events-auto z-[10001]">
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          className={cn(
            // sizing — match the anchor width
            'group/combobox-content w-(--anchor-width) overflow-hidden',
            // shape + surface (matches Select/Combobox content)
            'rounded-lg border border-space-08 bg-white shadow-lg',
            // animation
            'origin-(--transform-origin) duration-100',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
            'data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
            className,
          )}
          {...props}>
          {children}
        </ComboboxPrimitive.Popup>
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
};
